import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Anchor, Lock } from "lucide-react";

const SHIP = "https://static.prod-images.emergentagent.com/jobs/be4edc16-78ac-4cae-a990-8d444cf80e35/images/f9683f4580a37830cbac4d9c263e264f5d425a2268a0fb39259b5982918033ef.jpeg";

export default function Login() {
  const { login, setupRequired, setupFirstAdmin } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) nav("/"); else setError(r.error);
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    const r = await setupFirstAdmin(email, password, name);
    setBusy(false);
    if (r.ok) nav("/"); else setError(r.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Ship panel */}
      <div className="relative hidden lg:block">
        <img src={SHIP} alt="Endurance" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-deepnavy/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-serif text-2xl italic text-white/90">"Protecting what you have built."</p>
          <div className="mt-2 h-px w-24 bg-sky/60" />
          <p className="mt-3 text-sm text-sky/80 max-w-md">Private internal operating console for Endurance Casualty Insurance Agency. Authorized personnel only.</p>
        </div>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center bg-deepnavy lg:bg-cloud px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-8">
            <Anchor className="h-8 w-8 text-atlantic lg:text-navy" />
            <div>
              <div className="font-serif text-3xl tracking-wide text-white lg:text-navy">ENDURANCE</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-sky/70 lg:text-mutedsea">Casualty Insurance Agency</div>
            </div>
          </div>
          <h1 className="font-serif text-3xl text-white lg:text-navy mb-1">{setupRequired ? "Create administrator" : "Sign in"}</h1>
          <p className="text-sm text-sky/70 lg:text-mutedsea mb-6">{setupRequired ? "First-time setup: create the owner administrator account." : "Access the agency operating system."}</p>

          {setupRequired ? (
            <form onSubmit={submitSetup} className="space-y-4" data-testid="setup-form">
              <div>
                <Label className="text-xs text-sky/80 lg:text-slate mb-1 block">Full name</Label>
                <Input data-testid="setup-name" value={name} autoFocus
                  onChange={(e) => setName(e.target.value)} className="bg-white h-11" placeholder="G. Goldman (Owner)" />
              </div>
              <div>
                <Label className="text-xs text-sky/80 lg:text-slate mb-1 block">Email</Label>
                <Input data-testid="setup-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} className="bg-white h-11" placeholder="you@endurancecasualty.com" />
              </div>
              <div>
                <Label className="text-xs text-sky/80 lg:text-slate mb-1 block">Password</Label>
                <Input data-testid="setup-password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className="bg-white h-11" placeholder="At least 8 characters" />
              </div>
              <div>
                <Label className="text-xs text-sky/80 lg:text-slate mb-1 block">Confirm password</Label>
                <Input data-testid="setup-confirm" type="password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} className="bg-white h-11" placeholder="Re-enter password" />
              </div>
              {error && <div data-testid="login-error" className="text-sm text-seared bg-seared/10 border border-seared/30 rounded-sm px-3 py-2">{error}</div>}
              <Button data-testid="setup-submit" type="submit" disabled={busy}
                className="w-full h-11 bg-atlantic hover:bg-ocean text-white font-medium">
                <Lock className="h-4 w-4 mr-2" /> {busy ? "Creating…" : "Create administrator"}
              </Button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs text-sky/80 lg:text-slate mb-1 block">Email</Label>
              <Input data-testid="login-email" type="email" value={email} autoFocus
                onChange={(e) => setEmail(e.target.value)} className="bg-white h-11" placeholder="you@endurancecasualty.com" />
            </div>
            <div>
              <Label className="text-xs text-sky/80 lg:text-slate mb-1 block">Password</Label>
              <Input data-testid="login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} className="bg-white h-11" placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="text-sm text-seared bg-seared/10 border border-seared/30 rounded-sm px-3 py-2">{error}</div>}
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full h-11 bg-atlantic hover:bg-ocean text-white font-medium">
              <Lock className="h-4 w-4 mr-2" /> {busy ? "Signing in…" : "Sign In"}
            </Button>
          </form>
          )}
          <p className="text-[11px] text-sky/50 lg:text-mutedsea mt-6">This is a private system. No public registration. All access is logged and audited.</p>
        </div>
      </div>
    </div>
  );
}
